'use client';

import type { CollectionItem } from '@chakra-ui/react';
import { Select as ChakraSelect, Portal, Spinner, useSelectContext } from '@chakra-ui/react';
import { forwardRef, useMemo } from 'react';
import { Checkbox } from './checkbox';
import { CloseButton } from './close-button';

interface SelectTriggerProps extends ChakraSelect.ControlProps {
    clearable?: boolean;
    loading?: boolean;
}

export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(function SelectTrigger(props, ref) {
    const { children, clearable, loading, ...rest } = props;
    return (
        <ChakraSelect.Control {...rest}>
            <ChakraSelect.Trigger ref={ref}>{children}</ChakraSelect.Trigger>
            <ChakraSelect.IndicatorGroup>
                {loading ? <Spinner size="xs" borderWidth="1.5px" color="fg.muted" /> : clearable && <SelectClearTrigger />}
                <ChakraSelect.Indicator />
            </ChakraSelect.IndicatorGroup>
        </ChakraSelect.Control>
    );
});

const SelectClearTrigger = forwardRef<HTMLButtonElement, ChakraSelect.ClearTriggerProps>(function SelectClearTrigger(props, ref) {
    return (
        <ChakraSelect.ClearTrigger asChild {...props} ref={ref}>
            <CloseButton size="xs" variant="plain" focusVisibleRing="inside" focusRingWidth="2px" pointerEvents="auto" />
        </ChakraSelect.ClearTrigger>
    );
});

interface SelectContentProps extends ChakraSelect.ContentProps {
    portalled?: boolean;
    portalRef?: React.RefObject<HTMLElement>;
}

export const SelectContent = forwardRef<HTMLDivElement, SelectContentProps>(function SelectContent(props, ref) {
    const { portalled = false, portalRef, ...rest } = props;
    return (
        <Portal disabled={!portalled} container={portalRef}>
            <ChakraSelect.Positioner>
                <ChakraSelect.Content bg={{ _dark: 'musa.dark.500' }} {...rest} ref={ref} />
            </ChakraSelect.Positioner>
        </Portal>
    );
});

export const SelectItem = forwardRef<HTMLDivElement, ChakraSelect.ItemProps>(function SelectItem(props, ref) {
    const { item, children, ...rest } = props;
    return (
        <ChakraSelect.Item key={item.value} item={item} {...rest} ref={ref}>
            {children}
            <ChakraSelect.ItemIndicator />
        </ChakraSelect.Item>
    );
});

export const CheckboxSelectItem = forwardRef<HTMLDivElement, ChakraSelect.ItemProps & { disabled?: boolean }>(
    function SelectItem(props, ref) {
        const { item, children, disabled, ...rest } = props;
        const context = useSelectContext();
        const isSelected = context.selectedItems.some((selectedItem) => selectedItem.value === item.value);
        const disabledProps = useMemo(
            () => ({
                pointerEvents: 'none',
                opacity: 0.4,
                cursor: 'not-allowed',
            }),
            [],
        );
        return (
            <ChakraSelect.Item
                key={item.value}
                item={item}
                {...(disabled ? disabledProps : {})}
                justifyContent="flex-start"
                {...rest}
                ref={ref}>
                <Checkbox checked={isSelected} colorPalette="purple" />
                {children}
            </ChakraSelect.Item>
        );
    },
);

interface SelectValueTextProps extends Omit<ChakraSelect.ValueTextProps, 'children'> {
    children?(items: CollectionItem[]): React.ReactNode;
}

export const SelectValueText = forwardRef<HTMLSpanElement, SelectValueTextProps>(function SelectValueText(props, ref) {
    const { children, ...rest } = props;
    return (
        <ChakraSelect.ValueText {...rest} ref={ref}>
            <ChakraSelect.Context>
                {(select) => {
                    const items = select.selectedItems;
                    if (items.length === 0 && props.placeholder) {
                        return props.placeholder;
                    }
                    if (children) {
                        return children(items);
                    }
                    if (items.length === 1) {
                        return select.collection.stringifyItem(items[0]);
                    }
                }}
            </ChakraSelect.Context>
        </ChakraSelect.ValueText>
    );
});

export const SelectRoot = forwardRef<HTMLDivElement, ChakraSelect.RootProps>(function SelectRoot(props, ref) {
    return <ChakraSelect.Root {...props} ref={ref} positioning={{ sameWidth: true, ...props.positioning }} />;
});

interface SelectItemGroupProps extends ChakraSelect.ItemGroupProps {
    label: React.ReactNode;
}

export const SelectItemGroup = forwardRef<HTMLDivElement, SelectItemGroupProps>(function SelectItemGroup(props, ref) {
    const { children, label, ...rest } = props;
    return (
        <ChakraSelect.ItemGroup {...rest} ref={ref}>
            <ChakraSelect.ItemGroupLabel>{label}</ChakraSelect.ItemGroupLabel>
            {children}
        </ChakraSelect.ItemGroup>
    );
});

export const SelectLabel = ChakraSelect.Label;
export const SelectItemText = ChakraSelect.ItemText;
